import {
  Button, generateTableFromSchema, Message, Modal, Section, TableSkeleton, Tag,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useGetAirport } from '../../../services/airports/airportsQueries';
import styles from './AirportDetails.module.css';
import { deleteAirport } from '../../../services/airports/airportService';

const TerminalSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Terminal',
  properties: {
    name: {
      type: 'string',
      description: 'Name',
    },
    address: {
      type: 'string',
      description: 'Address',
    },
    phone: {
      type: 'string',
      description: 'Phone',
    },
    openingTime: {
      type: 'string',
      description: 'Opening time',
    },
    identityCode: {
      type: 'string',
      description: 'Identity Code',
    },
  },
  additionalProperties: false,
};

/**
 * Airport details component form for panel
 * @param {object} props
 * @param {string} props.airportId
 * @param {() => void} [props.handleClosePanel]
 * @returns {JSX.Element}
 */
export default function AirportDetails({ airportId, handleClosePanel }) {
  // local state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  // mutations
  const {
    mutate: deleteBookingMutate,
    isLoading: isLoadingDeleteBooking,
    error: deleteBookingError,
    isError: isErrorDeleteAirport,
  } = useMutation(() => deleteAirport(airportId), {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      handleClosePanel();
    },
  });

  // queries
  const { data: airport, isLoading } = useGetAirport(airportId, {
    options: {
      enabled: !!airportId,
    },
  });

  /**
   * ¨
   * @type {Terminal[]}
   */
  const terminals = Array.isArray(airport?.terminals) ? airport.terminals : [];

  const terminalRows = terminals.map((term) => ({
    name: term?.name ?? '',
    address: term?.address ?? '',
    phone: term?.phone ?? '',
    openingTime: term?.openingTime ?? '',
    identityCode: term?.identityCode ?? '',
  }));

  const handleConfirmDelete = () => {
    deleteBookingMutate();
  };

  const handleGoToEdit = () => {
    navigate(`/airports/${airportId}`);
  };

  // Extract error info safely for rendering
  // @ts-ignore
  const deleteErrorStatus = deleteBookingError?.response?.status;
  // @ts-ignore
  const deleteErrorCode = deleteBookingError?.response?.data?.code || deleteBookingError?.code;

  return (
    <div className="d-flex f-column full-screen-height">
      <div className="d-flex f-row f-wrap justify-between margin-bottom-m">
        <div className="d-flex f-row gap-s">
          <p className="h1-default margin-right-m">{airport?.name}</p>
          <Tag size="medium" label={airport?.code} />
        </div>
        <div className="d-flex f-row gap-s self-end">
          <Button
            onClick={handleGoToEdit}
            icon="edit"
            isLoading={isLoading}
          />
          <Button
            color="alert"
            icon="trash"
            onClick={() => setIsDeleteModalOpen(true)}
            isLoading={isLoadingDeleteBooking}
          />
        </div>
      </div>
      <Section
        id="terminals-section"
        title={t('airportDetails.terminalsSectionTitle')}
        isDisplay
      >
        {
          isLoading
            ? <TableSkeleton />
            : (
              <div className={styles['large-table']}>
                {terminalRows.length === 0
                  ? <div>{t('airportDetails.noTerminals') || 'No terminals'}</div>
                  : generateTableFromSchema({
                    JSONSchema: TerminalSchema,
                    data: terminalRows,
                  })}
              </div>
            )
        }
      </Section>

      {isDeleteModalOpen ? (
        <Modal
          id="deleteBookingModal"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title={t('bookingList.delete.title')}
          size="small"
        >
          {isErrorDeleteAirport ? (
            <Message>
              {deleteErrorStatus === 409
                ? t(`apiErrors.${deleteErrorCode || 'AIRPORT_USED_IN_FLIGHT_BRIEF'}`)
                : t(`apiErrors.${deleteErrorCode || 'generic'}`)}
            </Message>
          ) : null}
          <div className="d-flex f-column gap-m">
            <p className="p1-default">
              {t('bookingList.delete.description')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="alert"
                onClick={handleConfirmDelete}
                isLoading={isLoadingDeleteBooking}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
